import type { WindowNamesForApps, WindowNamesForAppsBody } from '../common/types/api'
import type { Tag } from '../common/types/common'
import type {
  IdentifierCountParams,
  IdentifierCountPayload,
  PerformanceReadingsParams,
  PerformanceReadingsPercentilesParams,
  PerformanceReadingsPercentilesPayload,
  PluginPingsDetailsParams,
  PluginPingsDetailsPayload,
  PluginPingsParams,
  PluginPingsPayload,
} from '../common/types/monitoring'
import type { WindowNavigationEvent } from '../features/BusinessApps/ThirdLayer/types'
import type {
  ActivityByApplicationData,
  ActivityData,
  ActivityInsightsBody,
  ComputerCountBody,
  DataflowData,
  UserInteractionEvents,
  UserInteractionEventsBody,
  WindowDataflowBody,
} from '../features/BusinessApps/types'
import type { RecommendationData } from '../features/Overview/types'
import type {
  PerformanceReadingCpu,
  PerformanceReadingMemory,
} from '../features/PerformanceMonitoringAdminPanelPage/types'
import type { AppUsageTrendData } from '../features/Processes/types'
import { apiSlice } from './apiSlice'
import { WEB_APP_URL_CORE_LOGIC_API } from './configs'

export const apiSliceCoreLogicApi = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    // **  RECOMMENDATIONS ** //
    getRecommendations: build.query<RecommendationData[], void>({
      query: () => ({
        url: `${WEB_APP_URL_CORE_LOGIC_API}/recommendations/results`,
        method: 'GET',
      }),
    }),
    // **  ACTIVITY INSIGHTS ** //
    getActivityInsightTimes: build.query<ActivityData[], ActivityInsightsBody>({
      query: (body) => ({
        url: `${WEB_APP_URL_CORE_LOGIC_API}/activity-insights`,
        method: 'POST',
        body,
      }),
    }),
    getActivityInsightTimesByApplication: build.query<
      ActivityByApplicationData[],
      ActivityInsightsBody
    >({
      query: (body) => ({
        url: `${WEB_APP_URL_CORE_LOGIC_API}/activity-insights/application`,
        method: 'POST',
        body,
      }),
    }),
    // **  WINDOW NAVIGATIONS ** //
    getWindowNavigations: build.query<
      { navigationsTo: WindowNavigationEvent[]; navigationsFrom: WindowNavigationEvent[] },
      any
    >({
      query: ({ body }) => ({
        url: `${WEB_APP_URL_CORE_LOGIC_API}/window_navigations/split_data`,
        method: 'POST',
        body,
      }),
    }),
    // **  WINDOW DATAFLOWS ** //
    getWindowDataflowsSource: build.query<DataflowData[], WindowDataflowBody>({
      query: (body) => ({
        url: `${WEB_APP_URL_CORE_LOGIC_API}/dataflows/window/source`,
        method: 'POST',
        body,
      }),
    }),
    getWindowDataflowsDestination: build.query<DataflowData[], WindowDataflowBody>({
      query: (body) => ({
        url: `${WEB_APP_URL_CORE_LOGIC_API}/dataflows/window/destination`,
        method: 'POST',
        body,
      }),
    }),
    getUserInteractionEvents: build.query<UserInteractionEvents, UserInteractionEventsBody>({
      query: (body) => ({
        url: `${WEB_APP_URL_CORE_LOGIC_API}/window_usage/user-interaction-event-counts`,
        method: 'POST',
        body,
      }),
    }),
    getTotalAppUsageTrend: build.query<AppUsageTrendData[], void>({
      query: () => ({
        url: `${WEB_APP_URL_CORE_LOGIC_API}/window_usage/total-app-usage-trend`,
        method: 'POST',
        body: {},
      }),
    }),
    getWindowNamesForApps: build.query<WindowNamesForApps[], WindowNamesForAppsBody>({
      query: (body) => ({
        url: `${WEB_APP_URL_CORE_LOGIC_API}/window_usage/app-window-names`,
        method: 'POST',
        body,
      }),
    }),
    // ** PROCESSES ** //
    getUniqueTags: build.query<Array<Tag[]>, void | { tags_of_interest: string[] }>({
      query: (body) => ({
        url: `${WEB_APP_URL_CORE_LOGIC_API}/window_usage/unique-tags`,
        method: 'POST',
        body: body ?? ['appname'],
      }),
    }),

    // ** MONITORING ** //
    getPluginPingsStatus: build.query<PluginPingsPayload, PluginPingsParams>({
      query: (body) => ({
        url: `${WEB_APP_URL_CORE_LOGIC_API}/monitoring/plugin-pings`,
        method: 'POST',
        body,
      }),
    }),
    getPluginPingsDetails: build.query<PluginPingsDetailsPayload, PluginPingsDetailsParams>({
      query: (body) => ({
        url: `${WEB_APP_URL_CORE_LOGIC_API}/monitoring/plugin-pings-details`,
        method: 'POST',
        body,
      }),
    }),
    getComputerCountData: build.query<number, ComputerCountBody>({
      query: (body) => ({
        url: `${WEB_APP_URL_CORE_LOGIC_API}/monitoring/computer-id-count`,
        method: 'POST',
        body,
      }),
    }),
    getIdentifierCount: build.query<IdentifierCountPayload, IdentifierCountParams>({
      query: (body) => ({
        url: `${WEB_APP_URL_CORE_LOGIC_API}/monitoring/identifier-count`,
        method: 'POST',
        body,
      }),
    }),
    getPerformanceReadings: build.query<
      PerformanceReadingCpu[] | PerformanceReadingMemory[],
      PerformanceReadingsParams
    >({
      query: (body) => ({
        url: `${WEB_APP_URL_CORE_LOGIC_API}/monitoring/performance-readings`,
        method: 'POST',
        body,
      }),
    }),
    getPerformanceReadingsPercentiles: build.query<
      PerformanceReadingsPercentilesPayload,
      PerformanceReadingsPercentilesParams
    >({
      query: (body) => ({
        url: `${WEB_APP_URL_CORE_LOGIC_API}/monitoring/performance-readings/percentiles`,
        method: 'POST',
        body,
      }),
    }),
  }),
})

export const {
  // **  RECOMMENDATIONS ** //
  useGetRecommendationsQuery,

  // **  ACTIVITY INSIGHTS ** //
  useGetActivityInsightTimesQuery,
  useGetActivityInsightTimesByApplicationQuery,

  // **  WINDOW NAVIGATIONS ** //
  useGetWindowNavigationsQuery,

  // **  WINDOW DATAFLOWS ** //
  useGetWindowDataflowsSourceQuery,
  useGetWindowDataflowsDestinationQuery,

  // **  USER INTERACTION EVENTS ** //
  useGetUserInteractionEventsQuery,

  // **  TOTAL APP USAGE TREND ** //
  useGetTotalAppUsageTrendQuery,

  // **  WINDOW NAMES FOR APPS ** //
  useGetWindowNamesForAppsQuery,

  // **  PROCESSES ** //
  useGetUniqueTagsQuery,

  // ** MONITORING ** //
  useGetPluginPingsStatusQuery,
  useGetPluginPingsDetailsQuery,
  useGetComputerCountDataQuery,
  useLazyGetComputerCountDataQuery,
  useGetIdentifierCountQuery,
  useGetPerformanceReadingsQuery,
  useGetPerformanceReadingsPercentilesQuery,
} = apiSliceCoreLogicApi
